// Schedule a reminder: appointment date + time in the customer's zone, when to send it,
// a template (rotating wordings) or free text, live preview, and "goes out Thu 9/3 at 6:00 PM".

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { countSegments, formatPhone, renderTemplate, unresolvedTokens, zonedTimeToUtc, localParts } from '@bt/core';
import { api, type Contact, type MeResponse } from '../../api';
import { Dialog } from '../../components/Dialog';
import { ContactPicker, type Picked } from '../../components/ContactPicker';
import { InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { fmtDateAtTime, zoneLabel } from '../../lib/format';
import { effectiveTimezone } from '../../lib/timezone';

type SendWhen = 'day_before_6pm' | 'two_hours_before' | 'custom';

export function ReminderDialog({
  me,
  contact: initialContact,
  lineId,
  onClose,
}: {
  me: MeResponse;
  contact?: Pick<Contact, 'id' | 'name' | 'phone_number' | 'timezone'> | null;
  lineId?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [contact, setContact] = useState(initialContact ?? null);
  const activeLines = me.lines.filter((l) => l.active !== false);
  const [line, setLine] = useState(lineId ?? activeLines[0]?.id ?? '');
  const [templateId, setTemplateId] = useState('');
  const [body, setBody] = useState('');
  const [apptDate, setApptDate] = useState('');
  const [apptTime, setApptTime] = useState('10:00');
  const [when, setWhen] = useState<SendWhen>('day_before_6pm');
  const [custom, setCustom] = useState('');
  const [creatingContact, setCreatingContact] = useState<string | null>(null);

  const templates = useQuery({ queryKey: ['templates'], queryFn: api.templates.list, staleTime: 30_000 });
  const template = templates.data?.items.find((t) => t.id === templateId) ?? null;

  const tz = effectiveTimezone(contact, me.workspace.timezone).tz;
  const zoneNote = tz !== me.workspace.timezone ? ` ${zoneLabel(tz)}` : '';

  // appointment + send time, all in the customer's zone
  const appt = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(apptDate);
    const t = /^(\d{2}):(\d{2})$/.exec(apptTime);
    if (!m || !t) return null;
    return zonedTimeToUtc({ year: +m[1], month: +m[2], day: +m[3], hour: +t[1], minute: +t[2] }, tz);
  }, [apptDate, apptTime, tz]);

  const sendAt = useMemo(() => {
    if (when === 'custom') {
      const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(custom);
      if (!m) return null;
      return zonedTimeToUtc({ year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5] }, tz);
    }
    if (!appt) return null;
    if (when === 'two_hours_before') return new Date(appt.getTime() - 2 * 3600_000);
    const p = localParts(appt, tz);
    const dayBefore = new Date(Date.UTC(p.year, p.month - 1, p.day - 1));
    return zonedTimeToUtc({ year: dayBefore.getUTCFullYear(), month: dayBefore.getUTCMonth() + 1, day: dayBefore.getUTCDate(), hour: 18, minute: 0 }, tz);
  }, [when, custom, appt, tz]);

  const vars: Record<string, string> = useMemo(
    () => ({
      name: contact?.name?.trim() ?? '',
      date: appt ? new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: tz }).format(appt) : '',
      time: appt ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz }).format(appt) : '',
      company: me.workspace.company_name ?? '',
    }),
    [contact, appt, tz, me.workspace.company_name],
  );

  const text = template ? template.variants[0] ?? '' : body;
  const missing = text ? unresolvedTokens(text, vars) : [];
  const preview = text ? renderTemplate(text, vars) : '';
  // WHY (review round 1, trap 5): the app rotates EVERY wording, so the cost is the longest one filled in
  const wordings = (template ? template.variants : text ? [text] : []).map((v) => countSegments(renderTemplate(v, vars)));
  const longest = wordings.reduce<ReturnType<typeof countSegments> | null>((m, w) => (m === null || w.length > m.length ? w : m), null);
  const inPast = sendAt ? sendAt.getTime() < Date.now() : false;

  const create = useMutation({
    mutationFn: () =>
      api.scheduled.create({
        contact_id: contact!.id,
        line_id: line || undefined,
        send_at: sendAt!.toISOString(),
        template_id: template ? template.id : undefined,
        body: template ? undefined : body.trim(),
        variables: vars,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['scheduled'] });
      toast({
        text: `Reminder set. Goes out ${fmtDateAtTime(res.scheduled?.effective_send_at ?? res.scheduled?.send_at ?? sendAt!.toISOString(), tz)}${zoneNote}.${res.policy_note ? ` ${res.policy_note}` : ''}${res.segment_note ? ` It ${res.segment_note}.` : ''}`,
        tone: (res.segments?.worst ?? 1) >= 2 ? 'default' : 'ok',
      });
      onClose();
    },
  });

  const pick = async (p: Picked) => {
    if (p.contact) {
      setContact(p.contact);
      return;
    }
    if (p.phone_number) {
      setCreatingContact(p.phone_number);
      try {
        const r = await api.contacts.create({ phone_number: p.phone_number });
        setContact(r.contact);
      } catch (e) {
        const id = (e as { details?: { contact_id?: string; id?: string } }).details;
        const existing = id?.contact_id ?? id?.id;
        if (existing) {
          const r = await api.contacts.get(existing);
          setContact(r.contact);
        } else toast({ text: (e as Error).message, tone: 'error' });
      } finally {
        setCreatingContact(null);
      }
    }
  };

  const canSave = !!contact && !!sendAt && !!text.trim() && missing.length === 0 && !create.isPending && !!line;

  return (
    <Dialog title="Schedule a reminder" onClose={onClose} wide>
      <label className="field">
        <span>To</span>
        {contact ? (
          <div className="row">
            <span className="grow">
              <strong>{contact.name || formatPhone(contact.phone_number)}</strong>
              {contact.name && <span className="muted small"> {formatPhone(contact.phone_number)}</span>}
            </span>
            {!initialContact && (
              <button className="btn small ghost" onClick={() => setContact(null)}>
                Change
              </button>
            )}
          </div>
        ) : (
          <ContactPicker autoFocus onPick={(p) => void pick(p)} />
        )}
        {creatingContact && <div className="muted small">Adding {formatPhone(creatingContact)}</div>}
      </label>

      {activeLines.length > 1 && (
        <label className="field">
          <span>Send from</span>
          <select className="input" value={line} onChange={(e) => setLine(e.target.value)}>
            {activeLines.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label ? `${l.label} · ` : ''}
                {formatPhone(l.phone_number)}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="row" style={{ alignItems: 'flex-start' }}>
        <label className="field grow">
          <span>Appointment date</span>
          <input className="input" type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} />
        </label>
        <label className="field grow">
          <span>Time{zoneNote ? ` (${zoneLabel(tz)})` : ''}</span>
          <input className="input" type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)} />
        </label>
      </div>

      <label className="field">
        <span>Send the reminder</span>
        <select className="input" value={when} onChange={(e) => setWhen(e.target.value as SendWhen)}>
          <option value="day_before_6pm">The day before at 6:00 PM</option>
          <option value="two_hours_before">2 hours before</option>
          <option value="custom">At a time I choose</option>
        </select>
        {when === 'custom' && <input className="input" type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} style={{ marginTop: 6 }} />}
      </label>

      <label className="field">
        <span>Wording</span>
        <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">Write my own</option>
          {templates.data?.items.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.variants.length} {t.variants.length === 1 ? 'wording' : 'wordings'})
            </option>
          ))}
        </select>
        {!template && (
          <textarea className="input" value={body} onChange={(e) => setBody(e.target.value)} style={{ marginTop: 6 }} placeholder="Hi {{name|there}}, this is {{company}}. Reminder: your appointment is {{date}} at {{time}}. Reply if you need to change it." />
        )}
        <div className="field-help">
          You can use {'{{name}}'}, {'{{date}}'}, {'{{time}}'}, {'{{company}}'}. Write {'{{name|there}}'} to fall back to “there” when there is no name.
        </div>
      </label>

      {missing.length > 0 && (
        <div className="notice danger" style={{ marginBottom: 8 }}>
          Missing {missing.map((m) => `{{${m}}}`).join(', ')}
          {missing.includes('name') ? ' — this contact has no name. Add one, or write {{name|there}}.' : missing.includes('company') ? ' — set the company name in Settings.' : ' — fill in the appointment above.'}
        </div>
      )}

      {preview && (
        <div className="card" style={{ background: 'var(--surface-2)' }}>
          <div className="tiny muted" style={{ marginBottom: 4 }}>
            Preview{template && template.variants.length > 1 ? ' (one of the wordings; the app rotates them)' : ''}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{preview}</div>
          {longest && (
            <div className={`tiny ${longest.segments >= 3 ? 'danger-text' : longest.segments === 2 ? 'warn-text' : 'muted'}`} style={{ marginTop: 4 }}>
              {wordings.length > 1 ? 'Longest wording: ' : ''}{longest.length} / {longest.capacity} · {longest.segments} {longest.segments === 1 ? 'segment' : 'segments'}
              {longest.encoding === 'ucs2' && ' · a special character makes this 70-character segments'}
              {longest.segments >= 2 && ' · costs double; shorten the wording under Templates'}
            </div>
          )}
          {sendAt && (
            <div className={`small ${inPast ? 'danger-text' : 'muted'}`} style={{ marginTop: 8 }}>
              {inPast ? 'That time has already passed.' : `Goes out ${fmtDateAtTime(sendAt.toISOString(), tz)}${zoneNote}. If that is outside texting hours it waits for the window.`}
            </div>
          )}
        </div>
      )}

      <InlineError error={create.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!canSave || inPast} onClick={() => create.mutate()}>
          {create.isPending ? 'Saving' : 'Schedule'}
        </button>
      </div>
    </Dialog>
  );
}
